import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ARTICLES } from "@/lib/blogData";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import { ArrowRight, Mail, CheckCircle2 } from "lucide-react";

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-[2rem] bg-espresso p-8 sm:p-14 relative overflow-hidden" data-testid="newsletter">
      <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-olive/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full bg-lime/15 blur-3xl pointer-events-none" />
      <div className="relative max-w-xl">
        <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-lime"><Mail size={14} /> The PaySignal Journal</span>
        <h2 className="font-serif font-light text-3xl sm:text-4xl tracking-tight text-cream mt-4">Get new articles in your inbox.</h2>
        <p className="text-cream/70 mt-3">Practical money tips for independent workers — a couple of times a month, no spam.</p>
        {done ? (
          <div className="mt-6 flex items-center gap-2 text-lime font-semibold" data-testid="newsletter-success">
            <CheckCircle2 size={20} /> You're subscribed — thanks for joining!
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" data-testid="newsletter-email"
              className="bg-cream/10 border-cream/20 text-cream placeholder:text-cream/50 sm:flex-1 rounded-full h-12 px-5" />
            <Button type="submit" disabled={busy} className="bg-lime text-espresso hover:bg-lime-dark rounded-full px-6 h-12" data-testid="newsletter-submit">
              {busy ? "Subscribing…" : "Subscribe"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Blog() {
  const [active, setActive] = useState("All");
  useSEO({
    title: "The PaySignal Journal — Money advice for freelancers & gig workers",
    description: "Practical, jargon-free articles on getting paid, tracking platform payouts, cash flow, taxes and retainers for independent workers.",
    canonical: window.location.origin + "/blog",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "The PaySignal Journal",
      "url": window.location.origin + "/blog",
      "blogPost": ARTICLES.map((a) => ({
        "@type": "BlogPosting",
        "headline": a.title,
        "description": a.excerpt,
        "datePublished": a.date,
        "url": window.location.origin + "/blog/" + a.slug,
        "articleSection": a.category,
      })),
    },
  });
  const categories = useMemo(() => ["All", ...Array.from(new Set(ARTICLES.map((a) => a.category)))], []);
  const list = active === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === active);
  const showFeatured = active === "All";
  const [featured, ...rest] = list;
  const grid = showFeatured ? rest : list;

  return (
    <div className="min-h-screen bg-cream text-espresso">
      <SiteHeader />

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-8">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#8C6D58]">
          <span className="w-6 h-px bg-[#8C6D58]" /> The PaySignal Journal
        </span>
        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 text-espresso max-w-2xl leading-[1.08]">
          Practical money advice for people who <em className="italic text-olive">work for themselves.</em>
        </h1>
        <p className="text-lg text-espresso-soft mt-5 max-w-xl leading-relaxed">
          Getting paid, tracking payouts, and staying on top of cash flow — no jargon, no fluff.
        </p>
        <div className="flex flex-wrap gap-2 mt-9" data-testid="category-filters">
          {categories.map((c) => (
            <button key={c} onClick={() => setActive(c)} data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                active === c ? "bg-espresso text-cream border-espresso" : "bg-white text-espresso-soft border-warmborder hover:bg-oatmeal"}`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      {showFeatured && featured && (
        <section className="max-w-6xl mx-auto px-6 pb-8">
          <Link to={`/blog/${featured.slug}`} data-testid={`article-${featured.slug}`}
            className="group grid md:grid-cols-2 gap-6 border border-warmborder rounded-[2rem] bg-white overflow-hidden hover:shadow-[0_30px_70px_-50px_rgba(42,36,33,0.5)] transition-shadow">
            <div className="h-56 md:h-full overflow-hidden">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-olive">{featured.category}</span>
              <h2 className="font-serif font-normal text-2xl sm:text-3xl tracking-tight mt-3 text-espresso leading-snug">{featured.title}</h2>
              <p className="text-espresso-soft mt-3 leading-relaxed">{featured.excerpt}</p>
              <p className="text-xs text-warmmuted mt-4 font-num">{featured.readTime}</p>
              <span className="inline-flex items-center gap-1 text-olive font-semibold text-sm mt-4 group-hover:gap-2 transition-all">Read article <ArrowRight size={15} /></span>
            </div>
          </Link>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {grid.map((a) => (
            <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`article-${a.slug}`}
              className="group border border-warmborder rounded-[1.5rem] bg-white overflow-hidden hover:shadow-[0_30px_70px_-55px_rgba(42,36,33,0.5)] hover:-translate-y-1 transition-all duration-300 flex flex-col">
              <div className="h-44 overflow-hidden">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-olive">{a.category}</span>
                <h3 className="font-serif font-medium text-lg tracking-tight mt-2 text-espresso leading-snug">{a.title}</h3>
                <p className="text-sm text-espresso-soft mt-2 flex-1 leading-relaxed">{a.excerpt}</p>
                <p className="text-xs text-warmmuted mt-4 font-num">{a.readTime}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <NewsletterForm />
      </section>

      <SiteFooter />
    </div>
  );
}
